import { useEffect, useState } from 'react'
import {
  InspectionBoardsIndexResponseComponent,
  ShippingStateValue,
} from '../../types/typescript-axios'

import { DefaultWithTitleLayout } from '../../components/templates/default-with-title-layout'
import { VixellTable } from '../../components/part/vixell-table'
import { TableHeaderContractAndManagementNo } from '../../components/part/table-headers/table-header-contract-and-management-no'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SearchInput } from '../../components/molecules/search-input'
import { Select } from '../../components/molecules/select'
import { Lang } from '../../translations/lang'
import { toFormattedDatetime } from '../../utils/to-formatted-datetime'
import { downloadCsv } from '../../utils/download-csv'
import { webRequest } from '../../types/handle-api'
import { usePaging } from '../../contexts/use-paging'

const ShippingStateSelects = [
  [Lang.all, ''] as const,
  [
    Lang.shippingStateName.STOCKED,
    ShippingStateValue.Stocked as string,
  ] as const,
  [
    Lang.shippingStateName.SHIPPED,
    ShippingStateValue.Shipped as string,
  ] as const,
  [
    Lang.shippingStateName.RETURNED,
    ShippingStateValue.Returned as string,
  ] as const,
  [
    Lang.shippingStateName.DISPOSED,
    ShippingStateValue.Disposed as string,
  ] as const,
]

export const AdminInspectionBoards = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [inspectionBoards, setInspectionBoards] = useState<
    InspectionBoardsIndexResponseComponent[]
  >([])

  const [searchWord, setSearchWord] = useState<string>('')
  const [searchShippingState, setSearchShippingState] = useState<string>('')
  const [from, setFrom] = useState<string>('')
  const [till, setTill] = useState<string>('')

  const [loadNextPageAt, setLoadNextPageAt] = useState<Date>(new Date())
  const { currentPage, setPaging, stopPageLoad } = usePaging()
  const [searchParamsString, setSearchParamsString] = useState<string>()

  useEffect(() => {
    fetchDataAndSetStates()
  }, [searchParams])

  async function fetchDataAndSetStates(loadNextPage = false) {
    const response = await fetchInspectionBoards({ loadNextPage })
    if (response.status === 200) {
      if (searchParams.toString() === searchParamsString) {
        setInspectionBoards((boards) => [
          ...boards,
          ...response.data.inspectionBoards,
        ])
      } else {
        setInspectionBoards(response.data.inspectionBoards)
      }
      setPaging(response.data)
      setSearchParamsString(searchParams.toString())
    }
  }

  async function fetchInspectionBoards({ all = false, loadNextPage = false }) {
    const query = searchParams.get('search')
    const shippingState = searchParams.get('shippingState')
    const latestShippingStateDateTimeFrom = searchParams.get('from')
    const latestShippingStateDateTimeTill = searchParams.get('till')

    setSearchWord((w) => w || query || '')
    setSearchShippingState(shippingState || '')
    setFrom(latestShippingStateDateTimeFrom || '')
    setTill(latestShippingStateDateTimeTill || '')

    return await webRequest('InspectionBoardsIndex', {
      ...(shippingState && {
        shippingState: shippingState as ShippingStateValue,
      }),
      ...(query && { query }),
      ...(latestShippingStateDateTimeFrom && {
        latestShippingStateDateTimeFrom,
      }),
      ...(latestShippingStateDateTimeTill && {
        latestShippingStateDateTimeTill,
      }),
      ...(all && { all: true }),
      ...(!all && loadNextPage && { page: currentPage + 1 }),
    })
  }

  async function csvDownload() {
    const response = await fetchInspectionBoards({ all: true })
    if (response.status === 200) {
      const csvData = [
        [
          Lang.inspectionBoardSerialNo,
          Lang.contractNo,
          Lang.managementNo,
          Lang.shippingState,
          Lang.shippingStateUpdatedDate,
        ],
        ...response.data.inspectionBoards.map(
          ({
            serialNo,
            fullContractNo,
            managementNo,
            shippingState,
            shippingStateUpdatedDateTime,
          }) => [
            serialNo,
            fullContractNo,
            managementNo,
            shippingState && Lang.shippingStateName[shippingState],
            toFormattedDatetime(shippingStateUpdatedDateTime),
          ]
        ),
      ]
      downloadCsv(csvData, `inspectionBoards-${new Date().toISOString()}.csv`)
    }
  }

  function infiniteLoad() {
    if (stopPageLoad) return
    if (moment(loadNextPageAt).isAfter(moment().subtract(3, 'seconds'))) return

    setLoadNextPageAt(new Date())
    fetchDataAndSetStates(true)
  }

  function selectShippingState(value: string) {
    if (value) searchParams.set('shippingState', value)
    if (!value) searchParams.delete('shippingState')
    navigate(`/inspection-boards?${searchParams.toString()}`)
  }

  function changeFrom(value: string) {
    if (value) searchParams.set('from', value)
    if (!value) searchParams.delete('from')
    navigate(`/inspection-boards?${searchParams.toString()}`)
  }

  function changeTill(value: string) {
    if (value) searchParams.set('till', value)
    if (!value) searchParams.delete('till')
    navigate(`/inspection-boards?${searchParams.toString()}`)
  }

  function search() {
    if (searchWord) searchParams.set('search', searchWord)
    if (!searchWord) searchParams.delete('search')
    navigate(`/inspection-boards?${searchParams.toString()}`)
  }

  const titleRightElement = (
    <div className="flex flex-col items-end">
      <div>
        <SearchInput
          value={searchWord}
          onChange={setSearchWord}
          onClick={search}
        />
      </div>
      <div className="flex mt-16 gap-[48px]">
        <div className="flex flex-col">
          <label>{Lang.shippingState}</label>
          <Select
            className="mt-8"
            value={searchShippingState}
            options={ShippingStateSelects}
            onChange={selectShippingState}
          />
        </div>
        <div>
          <label>{Lang.shippingStateUpdatedDate}</label>
          <div className="flex items-center gap-[12px]">
            <input
              type="date"
              className="w-[160px]"
              value={from}
              onChange={(e) => changeFrom(e.target.value)}
            />
            <span className="text-3xl text-gray-300">〜</span>
            <input
              type="date"
              className="w-[160px]"
              value={till}
              onChange={(e) => changeTill(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <DefaultWithTitleLayout
      title={Lang.admin.inspectionBoards.title}
      description={Lang.admin.inspectionBoards.description}
      titleRightElement={titleRightElement}
      infiniteLoad={infiniteLoad}
      csvDownload={csvDownload}
    >
      {inspectionBoards && (
        <VixellTable
          rootClassName="my-64"
          classNames={['w-200', 'w-200', 'w-200', 'w-200']}
          headers={[
            Lang.inspectionBoardSerialNo,
            <TableHeaderContractAndManagementNo key={1} />,
            Lang.shippingState,
            Lang.shippingState + Lang.updatedDate,
          ]}
          elements={inspectionBoards.map((inspectionBoard) => [
            inspectionBoard.serialNo,
            <div key={inspectionBoard.id}>
              <div>{inspectionBoard.fullContractNo}</div>
              <div>{inspectionBoard.managementNo}</div>
            </div>,
            inspectionBoard.shippingState &&
              Lang.shippingStateName[inspectionBoard.shippingState],
            inspectionBoard.shippingStateUpdatedDateTime &&
              moment(inspectionBoard.shippingStateUpdatedDateTime).format(
                Lang.dateFormat
              ),
          ])}
          onLineClick={(index) =>
            navigate(`/inspection-boards/${inspectionBoards[index].id}`)
          }
        />
      )}
    </DefaultWithTitleLayout>
  )
}
